<template>
  <div :class="[ns.b(), ns.is(status)]">
    <progress
      ref="progressRef"
      :class="[
        ns.e('content'),
        ns.e(stroke),
        ns.e(noRound ? '' : 'border-radius'),
        ns.e(indeterminate ? 'indeterminate' : ''),
        ns.e(indeterminate ? `indeterminate-${stroke}` : '')
      ]"
      :style="progressStyle"
      :value="percent"
      :max="100"
    ></progress>
    <span v-if="textInside" :class="ns.e('innerText')" :style="textInsideStyle">{{ percent }}%</span>
    <div
      v-if="(!hideInfo || $slots.default) && !textInside"
      :class="[ns.e('text'), ns.is(stroke)]"
      :style="{ fontSize: `${progressTextSize}px`, color: textColor }"
    >
      <slot v-bind="slotData">
        <span v-if="!status || stroke === 'custom'">{{ content }}</span>
        <sg-icon v-else><component :is="statusIcon" /></sg-icon>
      </slot>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, onMounted, watch } from 'vue'
import { SgIcon } from '@sgui-plus/components/icon'
import {
  Warning,
  Success,
  Error,
  Check,
  Close,
} from '@sgui-plus/icons-vue'
import { useResizeObserver, useDebounceFn  } from '@vueuse/core'
import { progressProps } from './progress'
import type { CSSProperties } from 'vue'
import { useNamespace } from '@sgui-plus/hooks'

export default defineComponent({
  name: 'SgProgress',
  components: {
    SgIcon,
    Warning,
    Success,
    Error,
    Check,
    Close,
  },
  props: progressProps,

  setup(props) {

    const ns = useNamespace('progress')
    const progressRef = ref<HTMLElement | null>(null)
    const progressWidth = ref<number>(0)

    const textInsideStyle = computed<CSSProperties>(() => {
      const position = props.percent ? progressWidth.value * props.percent / 100 - 40 : 0
      return {
        color: props.textColor,
        position: 'absolute',
        left: position + 'px',
      }
    })
    const progressStyle = computed<CSSProperties>(() => {
      return {
        height: props.strokeWidth + 'px',
        borderRadius: props.noRound ? 'inherit' : '100px'
      }
    })
    const statusIcon = computed(() => {
      if (props.status === 'warning') {
        return Warning
      }
      return props.status === 'success' ? Success : Error
    })
    const stroke = computed(() => {
      let ret: string
      switch (props.status) {
        case '':
          ret = 'primary'
          break
        case 'primary':
        case 'success':
        case 'warning':
          ret = props.status
          break
        case 'exception':
          ret = 'error'
          break
        default:
          ret = 'custom'
          setProperty(progressRef.value as HTMLElement, '--custom-progress-color', props.status)
      }
      return ret
    })
    const progressTextSize = computed(() => {
      return 12 + props.strokeWidth * 0.4
    })
    const content = computed(() => props.format(props.percent))
    const slotData = computed(() => {
      return {
        percent: props.percent,
      }
    })

    const setProperty = (dom: HTMLElement | null, attribute: string, value: string): void => {
      dom?.style?.setProperty(attribute, value)
    }
    const removeProgressValue = () => {
      let timer = setTimeout(() => {
        const progressDom = progressRef.value
        progressDom && progressDom.removeAttribute('value')
        clearTimeout(timer)
      })
    }
    const setDurationVar = () => {
      if (!props.indeterminate) return
      setProperty(progressRef.value as HTMLElement, '--progress-duration', props.duration + 's')
    }
    const setDurationAndProgressValue = () => {
      if (!props.indeterminate) return
      setDurationVar()
      removeProgressValue()
    }
    const setInnerColor = () => {
      setProperty(progressRef.value  as HTMLElement, '--progress-inner-color', props.innerColor || '#ebeef5')
    }

    watch(() => [props.indeterminate, props.duration], setDurationAndProgressValue)
    watch(() => props.innerColor, setInnerColor)

    onMounted(() => {
      setDurationAndProgressValue()
      setInnerColor()

      // console.log('progressRef123123', progressRef)
      useResizeObserver(progressRef.value as HTMLElement, useDebounceFn((entries) => {
        const entry = entries[0]
        const { width } = entry.contentRect
        progressWidth.value = width
      }, 100))
    })

    return {
      progressStyle,
      textInsideStyle,
      statusIcon,
      progressTextSize,
      content,
      slotData,
      stroke,
      progressRef,
      ns
    }
  },
})
</script>
